/**
 * Autovona storefront behaviour — vanilla JS, no dependencies.
 *
 * Ports the Next.js site's client logic (ui-context, cart context, Header,
 * AnnouncementBar, SearchOverlay, MiniCart, BackToTop, Reveal, NewsletterSignup)
 * onto Shopify primitives: the AJAX Cart API, the Section Rendering API and
 * predictive search. Exposes `window.Autovona` for page-level scripts
 * (bundle selector, frequently bought together, sticky buy bar…).
 *
 * Class-state convention: any element with `data-av-on="…"` / `data-av-off="…"`
 * swaps between those two class lists — the same class pairs the React
 * components toggled, so every transition is identical.
 */
(() => {
  'use strict';

  const cfg = window.AutovonaConfig || { routes: {}, sections: {} };
  const routes = Object.assign(
    {
      root: '/',
      cart: '/cart',
      cartAdd: '/cart/add.js',
      cartChange: '/cart/change.js',
      search: '/search',
      predictiveSearch: '/search/suggest',
    },
    cfg.routes
  );
  const sectionIds = Object.assign(
    { cartDrawer: 'autovona-cart-drawer', predictiveSearch: 'autovona-predictive-search' },
    cfg.sections
  );

  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const classList = (value) => (value || '').split(/\s+/).filter(Boolean);

  /** Swap an element between its data-av-on / data-av-off class lists. */
  function setState(el, on) {
    const onClasses = classList(el.dataset.avOn);
    const offClasses = classList(el.dataset.avOff);
    if (on) {
      el.classList.remove(...offClasses);
      el.classList.add(...onClasses);
    } else {
      el.classList.remove(...onClasses);
      el.classList.add(...offClasses);
    }
  }

  function setTreeState(root, on) {
    if (root.hasAttribute('data-av-on')) setState(root, on);
    $$('[data-av-on]', root).forEach((el) => setState(el, on));
  }

  const emit = (name, detail) => document.dispatchEvent(new CustomEvent(name, { detail }));

  /* ------------------------------------------------------------------ */
  /* Overlays — mobile menu / search / cart (ui-context.tsx)             */
  /* Only one open at a time; body scroll locked; Escape closes.         */
  /* ------------------------------------------------------------------ */
  const overlayEl = (name) => $(`[data-av-overlay="${name}"]`);
  const isOpen = (el) => el.getAttribute('aria-hidden') === 'false';

  function openOverlay(name) {
    const el = overlayEl(name);
    if (!el) return false;
    closeAllOverlays();
    setTreeState(el, true);
    el.setAttribute('aria-hidden', 'false');
    document.body.classList.add('av-no-scroll');
    if (name === 'cart' && drawerStale) Cart.refresh().catch(() => {});
    if (name === 'search') {
      window.setTimeout(() => $('[data-av-search-input]', el)?.focus(), 60);
    }
    emit('autovona:overlay', { name, open: true });
    return true;
  }

  function closeOverlay(name) {
    const el = overlayEl(name);
    if (!el || !isOpen(el)) return;
    setTreeState(el, false);
    el.setAttribute('aria-hidden', 'true');
    if (!$$('[data-av-overlay]').some(isOpen)) document.body.classList.remove('av-no-scroll');
    if (name === 'search') Search.reset();
    emit('autovona:overlay', { name, open: false });
  }

  function closeAllOverlays() {
    $$('[data-av-overlay]').forEach((el) => {
      if (isOpen(el)) closeOverlay(el.dataset.avOverlay);
    });
  }

  /* ------------------------------------------------------------------ */
  /* Toast (Toast.tsx) — used for cart errors                            */
  /* ------------------------------------------------------------------ */
  let toastTimer;
  function toast(message) {
    let el = $('[data-av-toast]');
    if (!el) {
      const wrap = document.createElement('div');
      wrap.className = 'av av-contents';
      wrap.innerHTML =
        '<div role="status" aria-live="polite" data-av-toast data-av-on="translate-y-0 opacity-100" data-av-off="pointer-events-none translate-y-4 opacity-0" class="fixed bottom-6 left-1/2 z-[70] flex -translate-x-1/2 items-center gap-3 rounded-btn border border-line bg-surface px-5 py-3.5 text-sm text-heading shadow-card-hover transition-all duration-300 pointer-events-none translate-y-4 opacity-0"><span data-av-toast-text></span></div>';
      document.body.appendChild(wrap);
      el = $('[data-av-toast]', wrap);
    }
    $('[data-av-toast-text]', el).textContent = message;
    setState(el, true);
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => setState(el, false), 3500);
  }

  /* ------------------------------------------------------------------ */
  /* Cart — Shopify AJAX Cart API + Section Rendering (cart.tsx)          */
  /* ------------------------------------------------------------------ */
  /* The mini cart re-renders only when needed: on the cart page a quantity change updates the page
     section alone (rendering both doubled the wait) and the drawer refreshes the next time it opens. */
  let drawerStale = false;

  function cartSectionIds(includeDrawer = true) {
    const ids = includeDrawer ? [sectionIds.cartDrawer] : [];
    $$('[data-av-cart-section]').forEach((el) => {
      const id = el.dataset.avCartSection;
      if (id && !ids.includes(id)) ids.push(id);
    });
    return ids;
  }

  function updateCount(count) {
    const n = Number(count) || 0;
    $$('[data-av-cart-count]').forEach((el) => {
      el.textContent = String(n);
      el.classList.toggle('av-hidden', n === 0);
    });
  }

  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  /* "Pairs well with your …": the mini cart renders one suggestion group per product in the
     cart; show the group for the product added most recently (remembered for this visit). */
  const LAST_ADDED = 'autovona:last-added';
  function rememberAdded(items) {
    try {
      window.sessionStorage.setItem(LAST_ADDED, items.map((i) => i.id).join(','));
    } catch (error) {
      /* storage unavailable (private mode) — the first group stays visible */
    }
  }
  function showPairGroup() {
    const groups = $$('[data-av-pair-group]');
    if (groups.length < 2) return;
    let ids = [];
    try {
      ids = (window.sessionStorage.getItem(LAST_ADDED) || '').split(',').filter(Boolean);
    } catch (error) {
      return;
    }
    const match = groups.find((g) => ids.some((id) => g.dataset.variants.includes(`,${id},`)));
    if (!match) return;
    groups.forEach((g) => g.classList.toggle('av-hidden', g !== match));
  }

  /* Variant ids just added: their lines in the mini cart get a short "added" highlight. */
  let justAdded = null;

  function renderSections(sections) {
    if (!sections) return;
    Object.entries(sections).forEach(([id, html]) => {
      if (!html) return;
      const doc = parseHTML(html);
      if (id === sectionIds.cartDrawer) {
        const next = $('[data-av-cart-drawer-content]', doc);
        const live = $('[data-av-cart-drawer-content]');
        if (next && live) {
          live.innerHTML = next.innerHTML;
          showPairGroup();
          if (justAdded) {
            $$('[data-variant-id]', live).forEach((line) => {
              if (line.matches('.av-x-drawer-line')) line.classList.toggle('is-added', justAdded.includes(line.dataset.variantId));
            });
            justAdded = null;
          }
          // Express checkout buttons arrive as fresh markup — ask Shopify to (re)draw them.
          if ($('[data-av-drawer-express]', live)) window.Shopify?.PaymentButton?.init?.();
        }
        const meta = $('[data-av-cart-meta]', doc);
        if (meta) updateCount(meta.dataset.count);
        return;
      }
      const live = $(`[data-av-cart-section="${id}"]`);
      const next = $(`[data-av-cart-section="${id}"]`, doc);
      if (live && next) live.innerHTML = next.innerHTML;
    });
    emit('autovona:cart:rendered', { sections: Object.keys(sections) });
  }

  async function postJSON(url, body) {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const message = data.description || data.message || 'Something went wrong. Please try again.';
      throw new Error(message);
    }
    return data;
  }

  const Cart = {
    /**
     * Add one or more lines. `items` = [{ id: variantId, quantity, properties? }].
     * Opens the mini cart afterwards (the site opens it on every add).
     */
    async add(items, { open = true } = {}) {
      rememberAdded(items);
      justAdded = items.map((item) => String(item.id));
      try {
        const data = await postJSON(routes.cartAdd, {
          items,
          sections: cartSectionIds().join(','),
          sections_url: window.location.pathname,
        });
        renderSections(data.sections);
        if (open) openOverlay('cart');
        emit('autovona:cart:added', { items });
        return data;
      } catch (error) {
        justAdded = null;
        toast(error.message);
        throw error;
      }
    },

    /** Set a line's quantity by line key (0 removes it). */
    async change(key, quantity) {
      try {
        const data = await postJSON(routes.cartChange, {
          id: key,
          quantity,
          sections: cartSectionIds().join(','),
          sections_url: window.location.pathname,
        });
        renderSections(data.sections);
        updateCount(data.item_count);
        emit('autovona:cart:changed', { key, quantity, cart: data });
        return data;
      } catch (error) {
        toast(error.message);
        throw error;
      }
    },

    /** Re-render cart UI from the server (e.g. after an app changed the cart). */
    async refresh() {
      const url = `${window.location.pathname}?sections=${encodeURIComponent(cartSectionIds().join(','))}`;
      const res = await fetch(url, { headers: { Accept: 'application/json' } });
      if (res.ok) {
        drawerStale = false;
        renderSections(await res.json());
      }
    },

    /** Set a line's quantity by its LINE NUMBER (keys go stale after every change). 0 removes it. */
    async changeLine(line, quantity) {
      const cartEl = overlayEl('cart');
      const withDrawer = !$('[data-av-cart-section]') || (cartEl && isOpen(cartEl));
      try {
        const data = await postJSON(routes.cartChange, {
          line,
          quantity,
          sections: cartSectionIds(withDrawer).join(','),
          sections_url: window.location.pathname,
        });
        if (!withDrawer) drawerStale = true;
        renderSections(data.sections);
        updateCount(data.item_count);
        emit('autovona:cart:changed', { line, quantity, cart: data });
        return data;
      } catch (error) {
        toast(error.message);
        Cart.refresh().catch(() => {});
        throw error;
      }
    },

    open: () => openOverlay('cart'),
    close: () => closeOverlay('cart'),
  };

  /* ------------------------------------------------------------------ */
  /* Quantity taps (+ / − / remove): the number changes instantly, quick  */
  /* taps are batched (one request per line, latest value wins) and sent  */
  /* one after another, so nothing races and no stale line key is used.   */
  /* ------------------------------------------------------------------ */
  const wantedQty = new Map();
  let flushTimer = null;
  let changing = Promise.resolve();

  function flushQty() {
    flushTimer = null;
    // Highest line first: removing a line only renumbers the lines below it.
    const batch = [...wantedQty].sort((a, b) => b[0] - a[0]);
    wantedQty.clear();
    changing = changing.then(async () => {
      for (const [line, qty] of batch) {
        try {
          await Cart.changeLine(line, qty);
        } catch (error) {
          break;
        }
      }
    });
  }

  function tapLine(button) {
    const line = Number(button.dataset.avLine);
    const scope = button.closest('li') || button.parentElement;
    const display = $('[data-av-qty-display]', scope);
    let target;
    if (button.hasAttribute('data-av-set')) {
      target = Number(button.dataset.avSet);
    } else {
      const base = Number(display?.textContent) || 1;
      const min = Number($('[data-av-min]', scope)?.dataset.avMin) || 1;
      const max = Number($('[data-av-max]', scope)?.dataset.avMax) || 99;
      target = Math.min(max, Math.max(min, base + Number(button.dataset.avDelta)));
      if (target === base) return;
      const minus = $('[data-av-delta="-1"]', scope);
      const plus = $('[data-av-delta="1"]', scope);
      if (minus) minus.disabled = target <= min;
      if (plus) plus.disabled = target >= max;
    }
    if (display) display.textContent = String(target);
    if (target === 0) {
      scope.classList.add('opacity-50');
      // Line numbers shift after a removal — freeze the list until the new one arrives.
      scope.closest('ul')?.setAttribute('data-av-locked', '');
    }
    wantedQty.set(line, target);
    window.clearTimeout(flushTimer);
    flushTimer = window.setTimeout(flushQty, target === 0 ? 0 : 220);
  }

  /* ------------------------------------------------------------------ */
  /* Search overlay with live suggestions (SearchOverlay.tsx)            */
  /* ------------------------------------------------------------------ */
  const Search = {
    controller: null,
    timer: null,

    els() {
      const root = overlayEl('search');
      if (!root) return null;
      return {
        root,
        form: $('[data-av-search-form]', root),
        input: $('[data-av-search-input]', root),
        results: $('[data-av-search-results]', root),
        nomatch: $('[data-av-search-nomatch]', root),
        popular: $('[data-av-search-popular]', root),
      };
    },

    show(state) {
      const e = this.els();
      if (!e) return;
      e.popular?.classList.toggle('av-hidden', state !== 'popular');
      e.nomatch?.classList.toggle('av-hidden', state !== 'nomatch');
      if (state !== 'results' && e.results) e.results.innerHTML = '';
    },

    reset() {
      const e = this.els();
      if (!e) return;
      this.controller?.abort();
      if (e.input) e.input.value = '';
      this.show('popular');
    },

    async update() {
      const e = this.els();
      if (!e?.input) return;
      const q = e.input.value.trim();
      if (q.length < 2) {
        this.controller?.abort();
        this.show('popular');
        return;
      }
      this.controller?.abort();
      this.controller = new AbortController();
      const params = new URLSearchParams({
        q,
        'resources[type]': 'product',
        'resources[limit]': '5',
        'resources[options][unavailable_products]': 'last',
        'resources[options][fields]': 'title,product_type,variants.title,vendor,tag,body',
        section_id: sectionIds.predictiveSearch,
      });
      try {
        const res = await fetch(`${routes.predictiveSearch}?${params}`, { signal: this.controller.signal });
        if (!res.ok) return;
        const doc = parseHTML(await res.text());
        const list = $('[data-av-search-has-results]', doc);
        if (list) {
          e.results.innerHTML = list.outerHTML;
          this.show('results');
        } else {
          this.show('nomatch');
        }
      } catch (error) {
        if (error.name !== 'AbortError') this.show('nomatch');
      }
    },

    init() {
      const e = this.els();
      if (!e?.form || e.form.dataset.avBound) return;
      e.form.dataset.avBound = '1';
      e.input?.addEventListener('input', () => {
        window.clearTimeout(this.timer);
        this.timer = window.setTimeout(() => this.update(), 120);
      });
      e.form.addEventListener('submit', (event) => {
        if (!e.input.value.trim()) event.preventDefault();
      });
      e.results?.addEventListener('click', (event) => {
        if (event.target.closest('[data-av-search-all]')) {
          event.preventDefault();
          e.form.requestSubmit ? e.form.requestSubmit() : e.form.submit();
        }
      });
    },
  };

  /* ------------------------------------------------------------------ */
  /* Newsletter / email capture — Shopify customer form, submitted in     */
  /* place; falls back to a normal post (captcha, errors).              */
  /* ------------------------------------------------------------------ */
  async function submitCustomerForm(form) {
    try {
      const res = await fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'text/html' },
      });
      if (res.ok && /customer_posted=true/.test(res.url)) return true;
    } catch (error) {
      /* fall through to a regular submit */
    }
    form.submit();
    return false;
  }

  function initNewsletterForms() {
    $$('form[data-av-newsletter]').forEach((form) => {
      if (form.dataset.avBound) return;
      form.dataset.avBound = '1';
      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        const body = $('[data-av-newsletter-body]', form);
        const template = $('template[data-av-newsletter-success]', form);
        const button = $('button[type="submit"]', form);
        if (button) button.disabled = true;
        const ok = await submitCustomerForm(form);
        if (ok && body && template) {
          body.replaceWith(template.content.cloneNode(true));
        } else if (button) {
          button.disabled = false;
        }
        emit('autovona:newsletter', { form, ok });
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Scroll-driven UI: header shadow (>8px), back-to-top (>400px)         */
  /* ------------------------------------------------------------------ */
  function initScrollUI() {
    const header = $('[data-av-header]');
    const backToTop = $('[data-av-back-to-top]');
    let ticking = false;

    const update = () => {
      ticking = false;
      const y = window.scrollY;
      header?.classList.toggle('shadow-header', y > 8);
      if (backToTop) setState(backToTop, y > 400);
    };

    window.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          ticking = true;
          window.requestAnimationFrame(update);
        }
      },
      { passive: true }
    );
    update();

    backToTop?.addEventListener('click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));
  }

  /* ------------------------------------------------------------------ */
  /* Scroll reveal (Reveal.tsx) — fade-in-up via IntersectionObserver    */
  /* ------------------------------------------------------------------ */
  function initReveal(root = document) {
    const targets = $$('[data-av-reveal]', root).filter((el) => !el.dataset.avRevealed);
    if (!targets.length) return;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduced || !('IntersectionObserver' in window)) {
      targets.forEach((el) => {
        el.dataset.avRevealed = '1';
        setState(el, true);
      });
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.dataset.avRevealed = '1';
          setState(entry.target, true);
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    targets.forEach((el) => observer.observe(el));
  }

  /* ------------------------------------------------------------------ */
  /* Recently viewed (recently-viewed.ts) — handles in localStorage,      */
  /* tiles rendered server-side via the Section Rendering API.           */
  /* ------------------------------------------------------------------ */
  const RECENT_KEY = 'autovona_recently_viewed';
  const RECENT_MAX = 8;

  function readRecent() {
    try {
      const list = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
      return Array.isArray(list) ? list.filter((h) => typeof h === 'string') : [];
    } catch (error) {
      return [];
    }
  }

  function recordView(handle) {
    if (!handle) return;
    const next = [handle, ...readRecent().filter((h) => h !== handle)].slice(0, RECENT_MAX);
    try {
      localStorage.setItem(RECENT_KEY, JSON.stringify(next));
    } catch (error) {
      /* storage unavailable */
    }
  }

  async function renderRecentlyViewed(root = document) {
    const base = routes.root.endsWith('/') ? routes.root : `${routes.root}/`;
    for (const section of $$('[data-av-recently-viewed]', root)) {
      const list = $('[data-av-recently-viewed-list]', section);
      if (!list) continue;
      const exclude = section.dataset.exclude;
      const limit = Number(section.dataset.limit) || 6;
      const handles = readRecent()
        .filter((h) => h !== exclude)
        .slice(0, limit);
      if (!handles.length) continue;
      const tiles = await Promise.all(
        handles.map(async (handle) => {
          try {
            const res = await fetch(`${base}products/${encodeURIComponent(handle)}?section_id=autovona-recent-tile`);
            if (!res.ok) return null;
            return $('[data-av-recent-tile]', parseHTML(await res.text()));
          } catch (error) {
            return null;
          }
        })
      );
      const found = tiles.filter(Boolean);
      if (!found.length) continue;
      list.replaceChildren(...found);
      section.classList.remove('av-hidden');
      // Swipeable row with arrows once the cards are in (the arrows hide when everything fits).
      const carousel = $('[data-av-recent-carousel]', section);
      if (carousel && !carousel.hasAttribute('data-av-carousel')) {
        carousel.setAttribute('data-av-carousel', '');
        initCarousels(section);
      }
    }
  }

  /* ------------------------------------------------------------------ */
  /* Currency selector — the Vitals app floats a fixed "AUD" pill in the  */
  /* bottom-left corner, which covers the sticky add-to-cart bar. It is   */
  /* moved into the header on desktop and into the mobile menu on phones. */
  /* ------------------------------------------------------------------ */
  function initCurrencySlot() {
    const slot = $('[data-av-currency-slot]');
    const deskSlot = $('[data-av-currency-slot-desktop]');
    const row = $('[data-av-currency-row]');
    if (!slot || !row || slot.dataset.avBound) return;
    slot.dataset.avBound = '1';
    const mq = window.matchMedia('(max-width: 1023px)');

    const place = () => {
      const widget = $('.vitals-currency-converter');
      if (!widget) return false;
      const target = mq.matches ? slot : deskSlot;
      if (target && widget.parentElement !== target) target.appendChild(widget);
      row.classList.toggle('av-hidden', !mq.matches);
      return true;
    };

    if (mq.addEventListener) mq.addEventListener('change', place);
    place();
    // The app injects its widget after load (and may re-create it) — watch <body>'s children.
    new MutationObserver(place).observe(document.body, { childList: true });
  }

  /* Account opens Shopify's hosted sign-in (several redirects). Warm the       */
  /* connection when the menu opens and show a spinner the instant it's tapped. */
  function initAccountLink() {
    document.addEventListener('autovona:overlay', (event) => {
      if (event.detail?.name !== 'menu' || !event.detail.open || !$('[data-av-account-link]')) return;
      if ($('link[data-av-preconnect]')) return;
      const link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = 'https://shopify.com';
      link.dataset.avPreconnect = '';
      document.head.appendChild(link);
    });
    // Back/forward cache restores the page with the spinner still on.
    window.addEventListener('pageshow', () => {
      $$('[data-av-pending]').forEach((el) => el.removeAttribute('data-av-pending'));
    });
  }

  /* ------------------------------------------------------------------ */
  /* Delegated clicks                                                    */
  /* ------------------------------------------------------------------ */
  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const accountLink = target.closest('[data-av-account-link]');
    if (accountLink) accountLink.setAttribute('data-av-pending', '');

    const opener = target.closest('[data-av-open]');
    if (opener) {
      if (openOverlay(opener.dataset.avOpen)) event.preventDefault();
      return;
    }

    const lineButton = target.closest('[data-av-line]');
    if (lineButton && !lineButton.disabled) {
      event.preventDefault();
      tapLine(lineButton);
      return;
    }

    const addButton = target.closest('[data-av-add]');
    if (addButton && !addButton.disabled) {
      event.preventDefault();
      const id = addButton.dataset.variantId;
      const quantity = Number(addButton.dataset.quantity || 1);
      if (!id) return;
      // On the cart page the page itself re-draws with the new item — stay there (no mini cart).
      const onCartPage = Boolean(addButton.closest('[data-av-cart-section]'));
      addButton.setAttribute('data-av-pending', '');
      Cart.add([{ id: Number(id), quantity }], { open: !onCartPage })
        .then(() => {
          if (onCartPage) toast('Added to your order');
        })
        .catch(() => {})
        .finally(() => addButton.removeAttribute('data-av-pending'));
      return;
    }

    const accordion = target.closest('[data-av-accordion]');
    if (accordion) {
      const open = accordion.getAttribute('aria-expanded') !== 'true';
      accordion.setAttribute('aria-expanded', String(open));
      $('svg', accordion)?.classList.toggle('rotate-180', open);
      if (accordion.nextElementSibling) setState(accordion.nextElementSibling, open);
      return;
    }

    const expand = target.closest('[data-av-expand-toggle]');
    if (expand) {
      const item = expand.closest('[data-av-expand]');
      const panel = item && $('[data-av-expand-panel]', item);
      const expanded = expand.getAttribute('aria-expanded') === 'true';
      expand.setAttribute('aria-expanded', String(!expanded));
      panel?.classList.toggle('av-hidden', expanded);
      $('svg', expand)?.classList.toggle('rotate-180', !expanded);
      return;
    }

    if (target.closest('[data-av-announcement-dismiss]')) {
      document.documentElement.classList.add('av-announcement-dismissed');
      try {
        // For this visit only — the bar is back on the next visit (see snippets/autovona-head.liquid).
        sessionStorage.setItem('autovona_announcement_dismissed', '1');
      } catch (error) {
        /* storage unavailable — dismiss for this page only */
      }
      return;
    }

    const closer = target.closest('[data-av-close]');
    if (closer) {
      const overlay = closer.closest('[data-av-overlay]');
      if (overlay) closeOverlay(overlay.dataset.avOverlay);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeAllOverlays();
  });

  /* Keep the cart UI in sync when apps / Shopify standard actions change it. */
  document.addEventListener('shopify:cart:lines-update', () => Cart.refresh().catch(() => {}));

  /* Route Shopify's standard "open cart" action to the Autovona mini cart. */
  function configureStandardActions() {
    const actions = window.Shopify?.actions;
    if (!actions) return;
    try {
      actions.updateCart?.configure({ eventTarget: () => document });
      actions.openCart?.configure({
        async handler() {
          if (!openOverlay('cart')) window.location.href = routes.cart;
        },
      });
    } catch (error) {
      /* platform API unavailable — header cart link still works */
    }
  }

  /* ------------------------------------------------------------------ */
  /* Cart page reservation timer (CartTimer.tsx) — counts down from the   */
  /* configured minutes while the page is open; survives re-renders.     */
  /* ------------------------------------------------------------------ */
  let cartTimerLeft = null;
  function initCartTimer() {
    const first = $('[data-av-cart-timer]');
    if (!first || cartTimerLeft !== null) return;
    cartTimerLeft = (Number(first.dataset.minutes) || 12) * 60;
    const pad = (n) => String(n).padStart(2, '0');
    window.setInterval(() => {
      cartTimerLeft = Math.max(0, cartTimerLeft - 1);
      const text = `${pad(Math.floor(cartTimerLeft / 60))}:${pad(cartTimerLeft % 60)}`;
      $$('[data-av-cart-timer]').forEach((el) => {
        el.textContent = text;
      });
    }, 1000);
  }

  /* ------------------------------------------------------------------ */
  /* Swipe rows with arrow buttons ([data-av-carousel]): the arrows move  */
  /* one card at a time, grey out at either end and hide when everything  */
  /* already fits on screen.                                              */
  /* ------------------------------------------------------------------ */
  function initCarousels(scope = document) {
    $$('[data-av-carousel]', scope).forEach((root) => {
      if (root.dataset.avBound) return;
      root.dataset.avBound = '1';
      const track = $('[data-av-carousel-track]', root);
      if (!track) return;
      const prev = $('[data-av-carousel-prev]', root);
      const next = $('[data-av-carousel-next]', root);
      const step = () => {
        const card = track.firstElementChild;
        const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        return card ? card.getBoundingClientRect().width + gap : track.clientWidth;
      };
      const update = () => {
        const max = track.scrollWidth - track.clientWidth - 2;
        root.classList.toggle('av-x-carousel--static', max <= 0);
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= max;
      };
      prev?.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
      next?.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
      track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  }

  /* ------------------------------------------------------------------ */
  /* Cart page (phones): a slim "Total · Checkout" bar at the bottom while */
  /* the order summary's checkout button is off screen. The cart section  */
  /* re-renders after every change, so the watcher is re-attached then.   */
  /* ------------------------------------------------------------------ */
  let cartBarObserver = null;
  function initCartBar() {
    cartBarObserver?.disconnect();
    const bar = $('[data-av-cart-bar]');
    const anchor = $('[data-av-cart-checkout]');
    if (!bar || !anchor || !('IntersectionObserver' in window)) return;
    cartBarObserver = new IntersectionObserver(([entry]) => bar.classList.toggle('is-visible', !entry.isIntersecting));
    cartBarObserver.observe(anchor);
  }
  document.addEventListener('autovona:cart:rendered', () => initCartBar());

  /* Cart add-ons with options: the picked option decides what "+ Add" adds (and the price shown). */
  document.addEventListener('change', (event) => {
    const select = event.target instanceof Element && event.target.closest('[data-av-xsell-select]');
    if (!select) return;
    const card = select.closest('[data-av-xsell]');
    const button = card && $('[data-av-add]', card);
    if (button) button.dataset.variantId = select.value;
    const price = card && $('[data-av-xsell-price]', card);
    const option = select.selectedOptions[0];
    if (price && option?.dataset.price) price.textContent = option.dataset.price;
  });

  /* Collection "Sort by" — Shopify sorts server-side via ?sort_by=. */
  document.addEventListener('change', (event) => {
    const select = event.target instanceof Element && event.target.closest('[data-av-sort]');
    if (!select) return;
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', select.value);
    url.searchParams.delete('page');
    window.location.href = url.toString();
  });

  /* ------------------------------------------------------------------ */
  /* Email popup (EmailPopup.tsx): after a delay or on exit intent.      */
  /* Closing it hides it for a few days (Theme settings → Autovona →     */
  /* Email popup); subscribing hides it for good.                        */
  /* ------------------------------------------------------------------ */
  const POPUP_KEY = 'autovona_email_popup';
  function initEmailPopup() {
    const popup = $('[data-av-email-popup]');
    if (!popup || popup.dataset.avBound) return;
    popup.dataset.avBound = '1';
    const repeatDays = Number(popup.dataset.repeatDays) || 7;
    const remember = (value) => {
      try {
        localStorage.setItem(POPUP_KEY, value);
      } catch (error) {
        /* storage unavailable */
      }
    };
    const hide = () => {
      popup.classList.add('av-hidden');
      remember(String(Date.now() + repeatDays * 86400000));
    };
    popup.addEventListener('click', (event) => {
      if (event.target instanceof Element && event.target.closest('[data-av-popup-close]')) hide();
    });
    document.addEventListener('autovona:newsletter', (event) => {
      // A sign-up anywhere (footer or popup) means: never show the popup again.
      if (!event.detail?.ok) return;
      remember('subscribed');
      if (!popup.contains(event.detail.form)) popup.classList.add('av-hidden');
    });

    // Theme editor: show while the section is selected, never auto-open.
    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:section:select', (event) => {
        if (event.target.contains(popup)) popup.classList.remove('av-hidden');
      });
      document.addEventListener('shopify:section:deselect', (event) => {
        if (event.target.contains(popup)) popup.classList.add('av-hidden');
      });
      return;
    }

    // Logged-in customers who already get our emails never see it.
    if (popup.dataset.subscribed === 'true') return;
    try {
      // 'subscribed' = never again; a number = closed, hidden until that time. The old value '1'
      // (hidden forever) is ignored, so people who closed it before see it again.
      const saved = localStorage.getItem(POPUP_KEY);
      if (saved === 'subscribed') return;
      if (saved && saved !== '1' && Number(saved) > Date.now()) return;
    } catch (error) {
      /* storage unavailable — still show once this page view */
    }
    // The cart page is the last step before checkout — never interrupt it.
    if ($('#MainContent')?.dataset.template === 'cart') return;

    let opened = false;
    let waiting = false;
    const onLeave = (event) => {
      if (event.clientY <= 0) show();
    };
    // Someone with something in the cart is already buying: no popup for them.
    const cartCount = () => Number($('[data-av-cart-meta]')?.dataset.count) || 0;
    if (cartCount() > 0) return;
    // Product pages are where ad visitors decide — give them time with the product first:
    // at least 40s on phones and 30s on desktop (exit intent still works there). Elsewhere
    // phones wait at least 15s and desktop keeps the configured delay.
    const baseDelay = Number(popup.dataset.delay) || 8;
    const isPhone = window.matchMedia?.('(max-width: 767px)').matches;
    const onProduct = Boolean($('[data-av-product]'));
    const minDelay = onProduct ? (isPhone ? 40 : 30) : isPhone ? 15 : 0;
    const timer = window.setTimeout(() => show(), Math.max(baseDelay, minDelay) * 1000);
    // Adding to cart cancels it for this visit.
    document.addEventListener('autovona:cart:added', () => {
      opened = true;
      window.clearTimeout(timer);
      document.removeEventListener('mouseout', onLeave);
    });
    function show() {
      if (opened || cartCount() > 0) return;
      // Don't pop over the menu, search or cart the visitor is using — try again shortly.
      if ($$('[data-av-overlay]').some(isOpen)) {
        if (!waiting) {
          waiting = true;
          window.setTimeout(() => {
            waiting = false;
            show();
          }, 4000);
        }
        return;
      }
      opened = true;
      window.clearTimeout(timer);
      document.removeEventListener('mouseout', onLeave);
      popup.classList.remove('av-hidden');
    }
    if (popup.dataset.exitIntent === 'true') document.addEventListener('mouseout', onLeave);
  }

  function init() {
    initEmailPopup();
    initCartTimer();
    initCurrencySlot();
    initAccountLink();
    showPairGroup();
    initCarousels();
    initCartBar();
    Search.init();
    initNewsletterForms();
    initScrollUI();
    initReveal();
    configureStandardActions();
    $$('[data-av-product-view]').forEach((el) => recordView(el.dataset.avProductView));
    renderRecentlyViewed();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  /* Theme editor: re-bind after a section is re-rendered. */
  document.addEventListener('shopify:section:load', (event) => {
    initReveal(event.target);
    renderRecentlyViewed(event.target);
    initCarousels(event.target);
    if (event.target.querySelector?.('[data-av-currency-slot]')) initCurrencySlot();
    if (event.target.querySelector?.('[data-av-search-form]')) Search.init();
    if (event.target.querySelector?.('form[data-av-newsletter]')) initNewsletterForms();
  });

  window.Autovona = { recordView, renderRecentlyViewed, cart: Cart, openOverlay, closeOverlay, closeAllOverlays, setState, toast, initReveal };
})();
